import LandingNav from '../components/LandingNav';
import LandingFooter from '../components/LandingFooter';
import '../styles/landing.css';

const linkStyle = { color: 'var(--color-primary)', textDecoration: 'none' };

export default function TermsPage() {
  return (
    <div className="landing-root">
      <LandingNav solid />
      <div className="landing-section" style={{ paddingTop: 120 }}>
        <div className="landing-section-content" style={{ maxWidth: 760 }}>
          <h1 style={{ fontSize: 40, fontWeight: 800, color: 'var(--color-text-primary)', marginBottom: 8, letterSpacing: '-0.02em' }}>
            Terms of Service
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 48 }}>
            Effective date: October 19, 2026 &nbsp;·&nbsp; CrewCast Inc.
          </p>

          <Section title="1. Overview and Platform Description">
            <p>CrewCast Inc. ("CrewCast", "we", "us", or "our") provides a business-to-business (B2B) Software-as-a-Service (SaaS) platform accessible via crew-cast.co and our iOS mobile application. The service enables organizations ("Customers") to invite their employees or authorized personnel ("Users") to collect, review, and manage employee-generated content, including media uploads submitted under company-defined content briefs ("Moments").</p>
            <p>By accessing or using the CrewCast platform, Customer and Users agree to be bound by these Terms of Service ("Terms"). If you are using the platform on behalf of an employer or organization, you represent that you have the authority to bind that entity to these Terms.</p>
          </Section>

          <Section title="2. Account Eligibility & Access">
            <ul>
              <li><strong>Business use only</strong> — CrewCast is an invite-only professional platform designed for business use by employed adults. You must be at least 16 years of age (or older, depending on your local working age requirements) to hold an account.</li>
              <li><strong>Account provisioning</strong> — User accounts are provisioned via email invitations sent on behalf of Customer administrators.</li>
              <li><strong>Security</strong> — Users are responsible for maintaining the confidentiality of their authentication session and keychain credentials. You must notify us immediately of any unauthorized access or security breaches.</li>
            </ul>
          </Section>

          <Section title="3. User Content & License Grants">
            <SubHeading>3.1 Content Submissions ("Moments")</SubHeading>
            <p>Users may upload photos, videos, text, or other media ("User Content") in response to content briefs created by their employer.</p>
            <SubHeading>3.2 Ownership & Platform License</SubHeading>
            <ul>
              <li><strong>Ownership</strong> — As between CrewCast and the Customer/User, title and ownership of all uploaded User Content remains with the Customer or User (subject to applicable employer-employee contracts).</li>
              <li><strong>License to CrewCast</strong> — By uploading User Content, you grant CrewCast a non-exclusive, worldwide, royalty-free, fully paid-up license to host, store, stream, transmit, reformat, and display such content solely to provide, maintain, and support the core product features (including displaying submissions to company administrators and processing analytics) on behalf of Customer.</li>
            </ul>
            <SubHeading>3.3 Usage by Customer</SubHeading>
            <p>Customer administrators have the capability to review, approve, download, and publish User Content. CrewCast is not responsible for how Customer or its administrators utilize, distribute, or publish User Content outside of the CrewCast platform.</p>
          </Section>

          <Section title="4. Acceptable Use Policy">
            <p>When accessing or uploading content to CrewCast, you agree not to upload, post, or transmit any content or engage in behavior that:</p>
            <ul>
              <li>Infringes any third-party intellectual property, privacy, or publicity rights.</li>
              <li>Contains harmful, unlawful, defamatory, harassing, obscene, or threatening material.</li>
              <li>Uploads malicious code, viruses, or software intended to impair or intercept platform operation.</li>
              <li>Attempts to gain unauthorized access to accounts, private S3 buckets, or network segments belonging to other organizations using the platform.</li>
              <li>Reverse-engineers, decompiles, or scrapes the web or mobile applications.</li>
            </ul>
            <p>CrewCast reserves the right to remove any User Content or suspend accounts that violate these guidelines or breach safety standards.</p>
          </Section>

          <Section title="5. Employer Rewards & Payout Disclaimer">
            <ul>
              <li><strong>Third-party rewards</strong> — CrewCast tracks reward credits and submission metadata to power reporting tools for Customer administrators.</li>
              <li><strong>No financial processing</strong> — CrewCast does not collect payment information, process financial transactions, or handle monetary disbursements. All reward payouts, incentives, or compensation connected to platform participation are managed exclusively by Customer outside of the CrewCast platform. CrewCast holds zero liability for unpaid, incorrect, or disputed employer rewards.</li>
            </ul>
          </Section>

          <Section title="6. Service Availability & Intellectual Property">
            <ul>
              <li><strong>Platform ownership</strong> — CrewCast Inc. retains all rights, title, and interest in and to the platform, mobile apps, software code, UI design, trademarks, and analytics tools (excluding User Content).</li>
              <li><strong>Service availability</strong> — We strive for high availability via AWS infrastructure, but we do not guarantee uninterrupted or error-free operation. We reserve the right to suspend or modify features for planned maintenance, security updates, or technical reasons.</li>
            </ul>
          </Section>

          <Section title="7. Data Privacy, Storage & Retention">
            <ul>
              <li><strong>Privacy Policy alignment</strong> — Data collected through the platform is processed strictly in accordance with our <a href="/privacy" style={linkStyle}>Privacy Policy</a>.</li>
              <li><strong>Multi-tenant isolation</strong> — User Content and metadata are logically isolated by company tenants within AWS infrastructure. Users cannot access data belonging to other organizations.</li>
              <li><strong>Data retention & termination</strong> — We retain account data for as long as Customer's subscription remains active. Upon platform termination by Customer, Customer and User data (including DynamoDB records and S3 media files) will be permanently deleted within 90 days, except where longer retention is mandated by law.</li>
            </ul>
          </Section>

          <Section title="8. Limitation of Liability & Warranties">
            <SubHeading>8.1 Disclaimers</SubHeading>
            <p>THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO FITNESS FOR A PARTICULAR PURPOSE, MERCHANTABILITY, OR NON-INFRINGEMENT.</p>
            <SubHeading>8.2 Limitation of Liability</SubHeading>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, CREWCAST INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, OR FOR LOSS OF PROFITS, REVENUE, DATA, OR USE, INCURRED BY CUSTOMER, USERS, OR ANY THIRD PARTY. CREWCAST'S TOTAL CUMULATIVE LIABILITY ARISING FROM OR RELATED TO THESE TERMS SHALL BE LIMITED TO THE GREATER OF $100 USD OR THE AMOUNT PAID BY CUSTOMER TO CREWCAST IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
          </Section>

          <Section title="9. Termination">
            <ul>
              <li><strong>Termination by employer</strong> — Because user accounts are linked to company subscriptions, an individual User's access may be revoked or modified at any time by their employer's administrator or upon termination of the master agreement between CrewCast and Customer.</li>
              <li><strong>Termination by CrewCast</strong> — We reserve the right to terminate or suspend access immediately for any violation of these Terms or illegal platform use.</li>
            </ul>
          </Section>

          <Section title="10. Governing Law & Dispute Resolution">
            <p>These Terms shall be governed by and construed under the laws of the jurisdiction in which CrewCast Inc. is registered, without regard to conflict of law principles. Any legal suit, action, or proceeding arising out of or related to these Terms shall be instituted exclusively in the applicable courts of that jurisdiction.</p>
          </Section>

          <Section title="11. Changes to Terms">
            <p>We may revise these Terms from time to time. When updates occur, we will adjust the effective date above. For material modifications affecting platform access or liability, we will notify Customer administrators via email. Continued platform access following notice indicates acceptance of the updated Terms.</p>
          </Section>

          <Section title="12. Contact Information">
            <p>For legal inquiries, support requests, or questions regarding these Terms of Service, please contact us:</p>
            <p>
              <strong>CrewCast Inc.</strong><br />
              <a href="/contact" style={linkStyle}>crew-cast.co/contact</a>
            </p>
          </Section>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 12 }}>
        {title}
      </h2>
      <div style={{ fontSize: 15, color: 'var(--color-text-secondary)', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)', margin: '4px 0 -4px' }}>
      {children}
    </h3>
  );
}
